import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useIsMobile from '../hooks/useIsMobile';

const BACKEND_URL = import.meta.env.VITE_API_URL || 'https://logoilab.com/api';

const COLORS = {
  bg: '#0D0B1A',
  card: 'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.08)',
  text: '#E8E4F0',
  textMuted: '#8B85A0',
  accent: '#C084FC',
  gold: '#F5C542',
  emerald: '#10B981',
  cyan: '#06B6D4',
};

const fonts = {
  mono: "'JetBrains Mono', 'Fira Code', monospace",
  sans: "'Inter', -apple-system, sans-serif",
};

export default function EmergenceInterpretation({ metrics, aiConsent = false, onRequestConsent }) {
  const mobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);

  const generateInterpretation = useCallback(async () => {
    if (!metrics) return;
    if (!aiConsent) {
      if (onRequestConsent) onRequestConsent();
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const resp = await fetch(`${BACKEND_URL}/protocol/interpret`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metrics, language: i18n.language || 'es' }),
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      if (data.interpretation) {
        setInterpretation(data.interpretation);
        setLastGenerated(new Date());
        // Cache in sessionStorage
        sessionStorage.setItem('logos_emergence_interp', JSON.stringify({
          interpretation: data.interpretation,
          timestamp: Date.now(),
          metricsHash: JSON.stringify(metrics).length,
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [metrics]);

  // Load cached interpretation on mount
  useEffect(() => {
    const cached = sessionStorage.getItem('logos_emergence_interp');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        // Use cache if less than 10 minutes old
        if (Date.now() - parsed.timestamp < 600000) {
          setInterpretation(parsed.interpretation);
          setLastGenerated(new Date(parsed.timestamp));
        }
      } catch { /* ignore */ }
    }
  }, []);

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(192,132,252,0.08) 0%, rgba(6,182,212,0.05) 100%)',
    border: `1px solid rgba(192,132,252,0.20)`,
    borderRadius: 16,
    padding: 24,
    position: 'relative',
    overflow: 'hidden',
  };

  const shimmerKeyframes = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes pulse-glow {
      0%, 100% { box-shadow: 0 0 20px rgba(192,132,252,0.1); }
      50% { box-shadow: 0 0 40px rgba(192,132,252,0.25); }
    }
  `;

  return (
    <div style={cardStyle}>
      <style>{shimmerKeyframes}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🧠</span>
          <div>
            <h3 style={{
              margin: 0, fontFamily: fonts.mono, fontSize: 11,
              letterSpacing: '0.15em', color: COLORS.accent, textTransform: 'uppercase',
            }}>
              Interpretación del Logos
            </h3>
            <span style={{ fontFamily: fonts.mono, fontSize: 9, color: COLORS.textMuted }}>
              Powered by GPT-4o · Análisis profundo de tu estado de consciencia
            </span>
          </div>
        </div>
        <button
          onClick={generateInterpretation}
          disabled={loading}
          style={{
            background: loading ? 'rgba(192,132,252,0.1)' : 'linear-gradient(135deg, #C084FC 0%, #818CF8 100%)',
            border: 'none', borderRadius: 8, padding: '8px 16px',
            color: '#fff', fontFamily: fonts.mono, fontSize: 10,
            cursor: loading ? 'wait' : 'pointer', letterSpacing: '0.1em',
            transition: 'all 0.3s ease',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? t('misc.analyzing') : interpretation ? t('misc.regenerate') : t('misc.generateInterpretation')}
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <div style={{
            width: 60, height: 60, margin: '0 auto 16px',
            borderRadius: '50%',
            border: '2px solid rgba(192,132,252,0.2)',
            borderTopColor: COLORS.accent,
            animation: 'spin 1s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ fontFamily: fonts.mono, fontSize: 11, color: COLORS.textMuted, margin: 0 }}>
            {t('misc.analyzing')}
          </p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8, padding: 16, marginBottom: 16,
        }}>
          <p style={{ fontFamily: fonts.mono, fontSize: 10, color: '#EF4444', margin: 0 }}>
            {t('alerts.errorRetry', { error })}
          </p>
        </div>
      )}

      {/* Empty State */}
      {!interpretation && !loading && !error && (
        <div style={{ padding: '30px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>✦</div>
          <p style={{ fontFamily: fonts.sans, fontSize: 13, color: COLORS.textMuted, margin: '0 0 4px', lineHeight: 1.6 }}>
            {t('misc.interpretationPrompt')}
          </p>
          <p style={{ fontFamily: fonts.mono, fontSize: 10, color: COLORS.textMuted, margin: 0 }}>
            {t('misc.gptAnalysis', { count: Object.keys(metrics || {}).length })}
          </p>
        </div>
      )}

      {/* Interpretation Content */}
      {interpretation && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Summary */}
          <div style={{
            background: 'rgba(192,132,252,0.06)', borderRadius: 12, padding: 16,
            borderLeft: '3px solid #C084FC',
          }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 9, color: COLORS.accent, marginBottom: 6, letterSpacing: '0.1em' }}>
              ESTADO ACTUAL
            </div>
            <p style={{ fontFamily: fonts.sans, fontSize: 13, color: COLORS.text, margin: 0, lineHeight: 1.7 }}>
              {interpretation.summary}
            </p>
          </div>

          {/* Key Insight */}
          {interpretation.keyInsight && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(245,197,66,0.08) 0%, rgba(245,197,66,0.03) 100%)',
              borderRadius: 12, padding: 16,
              borderLeft: '3px solid #F5C542',
            }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 9, color: COLORS.gold, marginBottom: 6, letterSpacing: '0.1em' }}>
                💡 INSIGHT CLAVE
              </div>
              <p style={{ fontFamily: fonts.sans, fontSize: 13, color: COLORS.text, margin: 0, lineHeight: 1.7, fontWeight: 500 }}>
                {interpretation.keyInsight}
              </p>
            </div>
          )}

          {/* Prediction + Domain Analysis grid */}
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            {interpretation.prediction && (
              <div style={{
                background: 'rgba(6,182,212,0.06)', borderRadius: 12, padding: 14,
                borderLeft: '3px solid #06B6D4',
              }}>
                <div style={{ fontFamily: fonts.mono, fontSize: 9, color: COLORS.cyan, marginBottom: 6, letterSpacing: '0.1em' }}>
                  🔮 PREDICCIÓN
                </div>
                <p style={{ fontFamily: fonts.sans, fontSize: 12, color: COLORS.text, margin: 0, lineHeight: 1.6 }}>
                  {interpretation.prediction}
                </p>
              </div>
            )}
            {interpretation.domain_analysis && (
              <div style={{
                background: 'rgba(16,185,129,0.06)', borderRadius: 12, padding: 14,
                borderLeft: '3px solid #10B981',
              }}>
                <div style={{ fontFamily: fonts.mono, fontSize: 9, color: COLORS.emerald, marginBottom: 6, letterSpacing: '0.1em' }}>
                  🎯 DOMINIOS
                </div>
                <p style={{ fontFamily: fonts.sans, fontSize: 12, color: COLORS.text, margin: 0, lineHeight: 1.6 }}>
                  {typeof interpretation.domain_analysis === 'string'
                    ? interpretation.domain_analysis
                    : Object.values(interpretation.domain_analysis).join(' · ')}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          {interpretation.actions?.length > 0 && (
            <div style={{
              background: 'rgba(16,185,129,0.06)', borderRadius: 12, padding: 16,
              borderLeft: '3px solid #10B981',
            }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 9, color: COLORS.emerald, marginBottom: 10, letterSpacing: '0.1em' }}>
                ⚡ ACCIONES PRIORITARIAS
              </div>
              {interpretation.actions.map((action, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  marginBottom: i < interpretation.actions.length - 1 ? 10 : 0,
                }}>
                  <span style={{
                    fontFamily: fonts.mono, fontSize: 10,
                    background: COLORS.emerald, color: '#000',
                    borderRadius: '50%', width: 20, height: 20,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, fontWeight: 700,
                  }}>
                    {i + 1}
                  </span>
                  <p style={{ fontFamily: fonts.sans, fontSize: 12, color: COLORS.text, margin: 0, lineHeight: 1.5 }}>
                    {action}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Metaphor */}
          {interpretation.metaphor && (
            <div style={{
              textAlign: 'center', padding: '12px 20px',
              background: 'rgba(255,255,255,0.02)', borderRadius: 12,
              borderTop: '1px solid rgba(192,132,252,0.1)',
            }}>
              <p style={{
                fontFamily: fonts.sans, fontSize: 12, color: COLORS.textMuted,
                margin: 0, fontStyle: 'italic', lineHeight: 1.6,
              }}>
                "{interpretation.metaphor}"
              </p>
            </div>
          )}

          {/* Timestamp */}
          {lastGenerated && (
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontFamily: fonts.mono, fontSize: 9, color: COLORS.textMuted }}>
                Generado: {lastGenerated.toLocaleTimeString()} · GPT-4o
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
